import {
  ChevronDownIcon,
  ChevronRightIcon,
  EyeNoneIcon,
  EyeOpenIcon,
} from '@radix-ui/react-icons'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { useKestrel } from 'features/kestrelViewer/KestrelProvider'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { KVAnnotation } from 'types'

const SectionSubtitleRow = styled.div<{ $isDragOver?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: sticky;
  top: 0;
  height: min-content;
  background-color: ${({ $isDragOver }) =>
    $isDragOver ? 'rgba(76, 175, 80, 0.2)' : 'transparent'};
  border: ${({ $isDragOver }) =>
    $isDragOver ? '2px solid rgba(76, 175, 80, 0.5)' : '2px solid transparent'};
  border-radius: 4px;
  padding: 0.25rem;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
`

export const SectionSubtitle = styled.div`
  font-size: 0.875rem;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.forecast.text1};
  font-weight: bold;
  max-width: 80%;
`

const StyledEyeClosedIcon = styled(EyeNoneIcon)`
  color: lightgray;
  &:hover {
    color: white;
  }
`

const StyledEyeOpenIcon = styled(EyeOpenIcon)`
  color: lightgray;
  &:hover {
    color: white;
  }
`

const StyledVisibilityToggle = styled.div`
  cursor: pointer;
  display: flex;
  gap: 4px;
  margin-left: auto;
  align-items: center;
`

const StyledChevronIcon = styled.div`
  color: lightgray;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    color: white;
  }
`

export const AnnotationGroupLabel: FC<{
  group: string
  annotations: KVAnnotation[]
  setHiddenGroups: React.Dispatch<React.SetStateAction<string[]>>
  onDropAnnotations?: (
    annotationIds: string[],
    targetGroup: string | null
  ) => void
  historical?: boolean
}> = ({
  group,
  annotations,
  setHiddenGroups,
  onDropAnnotations,
  historical,
}) => {
  const {
    sendKVCommand,
    annotations: allAnnotations,
    setAnnotations,
    historicalAnnotations: allHistoricalAnnotations,
    setHistoricalAnnotations,
  } = useKestrel()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const allHidden = useMemo(() => {
    return annotations.every((a) => !a.visible)
  }, [annotations])

  const handleHover = () => {
    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.HIGHLIGHT_GROUP,
        group: group,
      })
    }
  }

  const handleLeave = () => {
    if (sendKVCommand) {
      sendKVCommand({
        command: KestrelCommands.UNHIGHLIGHT_GROUP,
        group: group,
      })
    }
  }

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation()
    setHiddenGroups((prev) => {
      return prev.includes(group)
        ? prev.filter((item) => item !== group)
        : [...prev, group]
    })
    setIsCollapsed(!isCollapsed)
  }

  const toggleGroupVisibility = (e: React.MouseEvent) => {
    e.stopPropagation()

    const newVisibility = allHidden
    const annotationIds = new Set(annotations.map((a) => a.id))
    const relevantAnnotations =
      (historical ? allHistoricalAnnotations : allAnnotations) ?? []

    relevantAnnotations.forEach((annotation) => {
      if (annotationIds.has(annotation.id)) {
        annotation.visible = newVisibility

        if (sendKVCommand) {
          if (annotation.geometry?.kind === 'polygon') {
            sendKVCommand({
              command: KestrelCommands.UPDATE_SURFACE_AREA,
              surfaceAreaId: annotation.id,
              surfaceAreaVisible: newVisibility,
            })
          } else {
            sendKVCommand({
              command: KestrelCommands.UPDATE_ANNOTATION,
              annotationId: annotation.id,
              annotationVisible: newVisibility,
            })
          }
        }
      }
    })

    if (historical) {
      setHistoricalAnnotations([...relevantAnnotations])
    } else {
      setAnnotations([...relevantAnnotations])
    }

    if (sendKVCommand) {
      sendKVCommand({
        command: allHidden
          ? KestrelCommands.SHOW_GROUP
          : KestrelCommands.HIDE_GROUP,
        group: group,
      })
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const annotationIds = e.dataTransfer.getData('annotationIds')
    if (annotationIds && onDropAnnotations) {
      const ids = JSON.parse(annotationIds)
      onDropAnnotations(ids, group)
    }
  }

  return (
    <SectionSubtitleRow
      onMouseOver={handleHover}
      onMouseLeave={handleLeave}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      $isDragOver={isDragOver}>
      <SectionSubtitle>{group}</SectionSubtitle>
      <StyledVisibilityToggle>
        <TooltipWrapper
          side='top'
          offset={5}
          label={allHidden ? 'Show All' : 'Hide All'}>
          <div onClick={(e) => toggleGroupVisibility(e)}>
            {!allHidden ? (
              <StyledEyeOpenIcon width={18} height={18} />
            ) : (
              <StyledEyeClosedIcon width={18} height={18} />
            )}
          </div>
        </TooltipWrapper>
        <StyledChevronIcon onClick={(e) => toggleCollapse(e)}>
          {!isCollapsed ? (
            <ChevronDownIcon width={18} height={18} />
          ) : (
            <ChevronRightIcon width={18} height={18} />
          )}
        </StyledChevronIcon>
      </StyledVisibilityToggle>
    </SectionSubtitleRow>
  )
}
